<template>
  <teleport to="#app">
    <transition name="transition-fade" appear>
      <div
        v-show="open"
        class="net-choose-overlay__backdrop"
        @click.prevent.stop="close()"
      ></div>
    </transition>
  </teleport>
  <teleport to="#app">
    <transition
      name="transition-popup"
      appear
      @after-enter="onEntered()"
      @after-leave="end()"
    >
      <div v-show="open" class="net-choose-overlay" v-bind="$attrs" @click.stop="">
        <net-canvas
          ref="netElement"
          class="net-choose-overlay__net"
          mode="choose"
          :targets="targets ?? allTargets"
          :resources="resources ?? allResources"
          :indices="indices ?? allIndices"
          :flows="flows"
          :relations="relations"
          :flow-branches="flowBranches"
          :element-values="elementValues"
          :selection="canvasSelection"
          :net-transform="sceneTree.netTransform"
          :disabled-item-uids="disabledItemUids"
          :fixed-item="fixedElement"
          @select-item="onItemChoose"
          @add-select-item="onItemChoose"
          @select-many="addMany"
        />

        <div class="net-choose-overlay__choicebox">
          <span class="net-choose-overlay__choicebox__title">{{
            instructionText
          }}</span>
          <button
            class="net-choose-overlay__choicebox__button button button_straight"
            type="button"
            @click="close()"
          >
            <lucide-icon name="check" />
            <span class="ml-2">{{ $t("labels.confirm") }}</span>
          </button>

          <button
            class="net-choose-overlay__choicebox__abort button"
            type="button"
            @click="abort()"
          >
            {{ $t("labels.abort") }}
          </button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";

import NetCanvas from "./NetCanvas.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useElements } from "@editor/items/element/elements";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { isUidOfType, splitUid } from "@/helpers/utility/Id";
import { isElementTreeNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { useSceneTree } from "@editor/items/scene/sceneTree";

import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { CanvasSelection, NetSelectableItemType } from "./NetCanvasProps";
import type { ElementItem } from "@editor/items/element/Element";
import type { Identity, Uid } from "@/helpers/utility/Id";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<{
  targets?: { [targetId: number]: TargetItem };
  resources?: { [resourceId: number]: ResourceItem };
  indices?: { [indexId: number]: IndexItem };
  selection?: Identity<"target" | "resource" | "index">[];
  types?: ("target" | "resource" | "index")[];
  instructionText?: string;
  disabledItemUids?: Set<ElementItem["uid"]>;

  fixedElement?: {
    uid: Uid<"target" | "resource" | "index">;
  };
}>(), {
  types: () => ["target"],
  instructionText: "",
  disabledItemUids: () => new Set<ElementItem["uid"]>()
});

const emit = defineEmits<{
  (e: "choose", items: (TargetItem | ResourceItem | IndexItem)[]): void;
}>();

const sceneTree = useSceneTree();
const netElement = ref<InstanceType<typeof NetCanvas> | null>(null);

const open = ref(true);
const selectedItems = reactive({
  target: [] as number[],
  resource: [] as number[],
  index: [] as number[]
});

const canvasSelection = computed((): CanvasSelection => {
  return {
    selected: new Set([
      ...selectedItems.target.map((id) => `target_${id}` as Uid<"target">),
      ...selectedItems.resource.map((id) => `resource_${id}` as Uid<"resource">),
      ...selectedItems.index.map((id) => `index_${id}` as Uid<"index">)
    ]),
    highlighted: new Set<string>(),
    active: new Set<string>()
  };
});
const allTargets = computed(() => {
  return useTargetStore().items;
});
const allResources = computed(() => {
  return useResourceStore().items;
});
const allIndices = computed(() => {
  return useIndexStore().items;
});
const flows = computed(() => {
  return useFlowStore().items;
});
const flowBranches = computed(() => {
  return useFlowBranchStore().items;
});
const relations = computed(() => {
  return useRelationStore().items;
});

const elementValues = computed(() => useElements().elementValues);

watch(() => props.selection, () => {
  fillSelectedItemsFromSelection();
}, { immediate: true });
  
function onEntered() {
  const netRef = netElement.value;
  if (netRef && "updateDimensionVars" in netRef) {
    netRef.updateDimensionVars();
  }
}

function onItemChoose(item: { uid: Uid<NetSelectableItemType> }) {
  if (!item.uid) return;
  if (!isUidOfType(item.uid, "sceneNode")) return;

  select(item);
}

function select(item: { uid: Uid<NetSelectableItemType> }) {
  if (!item.uid) return;
  if (!isUidOfType(item.uid, "sceneNode")) return;

  const treeNode = sceneTree.treeNodes.get(item.uid);
  if (!treeNode) return;
  if (!isElementTreeNode(treeNode)) return;

  const sceneNode = treeNode.node;

  const { type: elementType, id: elementId } = splitUid(sceneNode.data.elementUid);

  // only handle allowed types
  if (!props.types.includes(elementType)) return;

  // never select disabled items
  if (props.disabledItemUids.has(sceneNode.data.elementUid)) {
    return;
  }

  if (selectedItems?.[elementType] == null) {
    selectedItems[elementType] = [];
  }

  const idsOfType = selectedItems[elementType] ?? [];
  
  if (idsOfType.includes(elementId)) {
    // deselect if already chosen
    idsOfType.splice(idsOfType.indexOf(elementId), 1);
    return;
  } else {
    idsOfType.push(elementId);
  }
}

function addMany(items: { uid: Uid<NetSelectableItemType> }[]) {
  if (items.length === 1) return onItemChoose(items[0]);

  for (const item of items) {
    select(item);
  }
}
function close() {
  open.value = false;
}

function end() {
  const selected: (TargetItem | ResourceItem | IndexItem)[] = [];
  for (const type of props.types) {
    const selectedIds = selectedItems[type] ?? [];
    if (type === "target") {
      selected.push(...useTargetStore().getArrayByIds(selectedIds));
    } else if (type === "index") {
      selected.push(...useIndexStore().getArrayByIds(selectedIds));
    } else if (type === "resource") {
      selected.push(...useResourceStore().getArrayByIds(selectedIds));
    }
  }
  emit("choose", selected);
}

function abort() {
  fillSelectedItemsFromSelection();
  close();
}

function fillSelectedItemsFromSelection() {
  const selection = props.selection;
  selectedItems.index = [];
  selectedItems.target = [];
  selectedItems.resource = [];
  if (selection) {
    for (const item of selection) {
      if (!selectedItems[item.type]) {
        selectedItems[item.type] = [];
      }
      selectedItems[item.type].push(item.id);
    }
  } else {
    selectedItems.index = [];
    selectedItems.target = [];
    selectedItems.resource = [];
  }
}
</script>

<style lang="scss">
.net-choose-overlay {
  position: fixed;
  left: 3rem;
  right: 2rem;
  bottom: 2rem;
  top: 3rem;
  z-index: 0;

  &__backdrop {
    background-color: var(--color-backdrop);
    z-index: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &__net {
    border-radius: 5px;
    box-shadow: 0 5px 20px var(--color-shadow-2);
  }

  &__choicebox {
    position: absolute;
    top: 0.5rem;
    left: 1rem;
    background-color: var(--color-background-close);
    display: grid;
    grid-template-columns: 1fr min-content min-content;
    border: 2px solid var(--color-border);
    border-radius: 20rem;
    overflow: hidden;

    &__title {
      padding: 0.5rem 1rem;
      min-width: 8rem;
      text-align: left;
      align-self: center;
    }

    &__button {
      border: none;
      z-index: 1;
      &:focus {
        background-color: var(--color-action);
        color: var(--color-action-contrast);
      }
    }
    &__abort {
      background-color: var(--color-background-deep);
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;

      &:hover {
        background-color: var(--color-background-close);
      }
    }
  }
}
</style>
